/**
 * High-level API client: one named method per endpoint. Components never call httpAPI directly.
 */
import { GET, POST, PUT } from './httpAPI'

const qs = (params = {}) => {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  return entries.length ? '?' + new URLSearchParams(entries).toString() : ''
}

const backend = {
  getHealth: () => GET('/api/health'),

  // setup + session
  getSetupStatus: () => GET('/api/config/status'),
  setupOwner: (data) => POST('/api/setup/owner', data),
  openGate: () => POST('/api/auth/gate'),
  getMe: () => GET('/api/auth/me'),
  verifyMfa: (data) => POST('/api/auth/mfa/verify', data),
  startMfaEnrol: () => POST('/api/auth/mfa/enrol/start'),
  confirmMfaEnrol: (code) => POST('/api/auth/mfa/enrol/confirm', { code }),
  keepAlive: () => POST('/api/auth/keepalive'),
  logout: () => POST('/api/auth/logout'),

  // customers + documents
  listCustomers: (params) => GET('/api/customers' + qs(params)),
  getCustomer: (id) => GET(`/api/customers/${id}`),
  createCustomer: (data) => POST('/api/customers', data),
  updateCustomer: (id, data) => PUT(`/api/customers/${id}`, data),
  revealIds: (id) => POST(`/api/customers/${id}/reveal-ids`),
  verifyIdentity: (id, status) => POST(`/api/customers/${id}/verify-identity`, { status }),
  uploadDocument: (customerId, file, docType, applicationId) => {
    const form = new FormData()
    form.append('doc_type', docType)
    if (applicationId) form.append('application_id', applicationId)
    form.append('file', file)
    return POST(`/api/customers/${customerId}/documents`, form, true)
  },
  reviewDocument: (id, data) => POST(`/api/documents/${id}/review`, data),
  documentUrl: (id) => `/api/documents/${id}/file`,

  // products + quotes
  listProducts: () => GET('/api/products'),
  listAdminProducts: () => GET('/api/admin/products'),
  updateProduct: (id, data) => PUT(`/api/admin/products/${id}`, data),
  createProduct: (data) => POST('/api/admin/products', data),
  getQuote: (data) => POST('/api/quotes', data),

  // applications
  listApplications: (params) => GET('/api/applications' + qs(params)),
  getApplication: (id) => GET(`/api/applications/${id}`),
  createApplication: (data) => POST('/api/applications', data),
  updateApplication: (id, data) => PUT(`/api/applications/${id}`, data),
  applicationAction: (id, action, data) => POST(`/api/applications/${id}/${action}`, data),
  getAgreement: (id) => GET(`/api/applications/${id}/agreement`),

  // loans + payments
  listLoans: (params) => GET('/api/loans' + qs(params)),
  listOverdue: () => GET('/api/loans/overdue'),
  listDue: () => GET('/api/loans/due'),
  getLoan: (id) => GET(`/api/loans/${id}`),
  loanAction: (id, action, data) => POST(`/api/loans/${id}/${action}`, data),
  listPayments: (params) => GET('/api/payments' + qs(params)),
  recordPayment: (data) => POST('/api/payments', data),
  paymentLink: (data) => POST('/api/payments/link', data),
  paymentAction: (id, action, data) => POST(`/api/payments/${id}/${action}`, data),
  getReceipt: (id) => GET(`/api/payments/${id}/receipt`),

  // dashboards + reports
  getAgentDashboard: () => GET('/api/dashboard/agent'),
  getAdminDashboard: () => GET('/api/dashboard/admin'),
  getReport: (name, params) => GET(`/api/reports/${name}` + qs(params)),
  reportCsvUrl: (name, params) => `/api/reports/${name}` + qs({ ...params, format: 'csv' }),

  // staff, audit, settings
  listAgents: () => GET('/api/agents'),
  createAgent: (data) => POST('/api/agents', data),
  updateAgent: (id, data) => PUT(`/api/agents/${id}`, data),
  resetMfa: (id) => POST(`/api/agents/${id}/reset-mfa`),
  listAudit: (params) => GET('/api/audit' + qs(params)),
  getSettings: () => GET('/api/settings'),
  saveSettings: (data) => PUT('/api/settings', data),
  runJobs: () => POST('/api/settings/jobs/run'),
}

export default backend
